import {
  EnvelopeIcon,
  InboxStackIcon,
  MagnifyingGlassIcon,
  RectangleStackIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import {
  type ActiveFilter,
  type FilterDefinition,
  type FilterGeneratorContext,
  type FilterSuggestion,
  type SerializedFilter
} from '@oysterjs/ui/search-filter';
import {
  type EmailBulkSendListFieldsFragment,
  MailboxAccountsDocument,
  type MailboxAccountsQuery,
  type MailboxAccountsQueryVariables,
  OutboxStatus as OutboxStatusFilterValue,
  type OutboxFilterInput
} from '~/api/graphql';
import { client } from '~/api/client';
import { BaseDateFilter, getSinceDate } from '~/api/data/filter-base';
import { getEmailBulkSends } from '~/api/data/email-bulk-send/queries';
import { OUTBOUND_EMAIL_STUCK_AFTER_MS } from '~/components/mailbox/outbound-emails-columns';
import { outboxStatusLabel } from '~/components/mailbox/outbox-columns';
import type { OutboxStatus } from '~/components/mailbox/outbox-rows';

/**
 * The chips the outbox understands. Every one of them is answered by the
 * server: the list is a merge of three paginated ledgers, so a chip that
 * filtered the rows already fetched would report the failures among the most
 * recent fifty emails as though they were all of them.
 */
export type OutboxFilterType = 'status' | 'search' | 'mailbox' | 'person' | 'date' | 'batch';

/**
 * The badge's vocabulary, in the enum the filter travels in.
 *
 * Keyed by the status the row renders rather than listed independently, so the
 * two cannot drift: a status added to the badge stops this compiling until it
 * has a filter value, and a chip can never name a state the list has no way to
 * show.
 *
 * `checking` and `mailboxDisconnected` are the two with nothing to map to. Both
 * are what this tab's own re-read of the provider copy turned up — one still
 * running, one that could not reach the mailbox — and no stored ledger can be
 * asked about either.
 */
export const OUTBOX_STATUS_FILTER_VALUES: Record<
  Exclude<OutboxStatus, 'checking' | 'mailboxDisconnected'>,
  OutboxStatusFilterValue
> = {
  composed: OutboxStatusFilterValue.Composed,
  inMailbox: OutboxStatusFilterValue.InMailbox,
  scheduled: OutboxStatusFilterValue.Scheduled,
  sending: OutboxStatusFilterValue.Sending,
  interrupted: OutboxStatusFilterValue.Interrupted,
  sent: OutboxStatusFilterValue.Sent,
  sentFromMailClient: OutboxStatusFilterValue.SentFromMailClient,
  failed: OutboxStatusFilterValue.Failed,
  cancelled: OutboxStatusFilterValue.Cancelled,
  replaced: OutboxStatusFilterValue.Replaced,
  withdrawn: OutboxStatusFilterValue.Withdrawn,
  goneUnproven: OutboxStatusFilterValue.GoneUnproven,
  closed: OutboxStatusFilterValue.Closed
};

/**
 * Each filterable status as it is offered and read back, in the badge's own
 * words, so a filter cannot return rows that call themselves something else.
 */
export const OUTBOX_STATUS_OPTIONS: { value: OutboxStatusFilterValue; label: string }[] =
  Object.entries(OUTBOX_STATUS_FILTER_VALUES).map(([status, value]) => ({
    value,
    label: outboxStatusLabel(status as OutboxStatus)
  }));

/**
 * The statuses that mean the email stopped being work in progress without
 * becoming mail, which the list hides unless asked. Named here as well as on
 * the row because asking for one of them by name has to override the switch
 * that hides them — a reader who filters to "Replaced" and is shown nothing
 * has been answered with the setting rather than with their question.
 */
const CLOSED_STATUS_FILTER_VALUES: ReadonlySet<OutboxStatusFilterValue> = new Set([
  OutboxStatusFilterValue.Replaced,
  OutboxStatusFilterValue.Withdrawn,
  OutboxStatusFilterValue.GoneUnproven,
  OutboxStatusFilterValue.Closed
]);

export class OutboxStatusFilter implements FilterDefinition<OutboxFilterType> {
  readonly type = 'status' as const;
  readonly icon = EnvelopeIcon;
  readonly priority = 0;
  // Several at once: "what needs my attention" is the failures and the ones
  // nobody could account for, which is two words and not one.
  readonly isUnique = false;
  private readonly statusOptions: { value: OutboxStatusFilterValue; label: string }[];

  constructor(isScheduledSendEnabled = false) {
    this.statusOptions = OUTBOX_STATUS_OPTIONS.filter(
      (option) => isScheduledSendEnabled || option.value !== OutboxStatusFilterValue.Scheduled
    );
  }

  generate({ query, activeFilters }: FilterGeneratorContext<OutboxFilterType>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    const chosen = new Set(
      activeFilters.filter((filter) => filter.type === 'status').map((filter) => filter.value)
    );
    return this.statusOptions
      .filter(
        (option) =>
          !chosen.has(option.value) &&
          (option.label.toLowerCase().includes(q) || 'status'.includes(q))
      )
      .map((option) => ({
        value: option.value,
        displayLabel: 'Status is',
        displayValue: option.label
      }));
  }

  deserialize(s: SerializedFilter<OutboxFilterType>): ActiveFilter<OutboxFilterType> | null {
    const option = this.statusOptions.find((candidate) => candidate.value === s.value);
    if (!option) {
      return null;
    }
    return {
      id: `status-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Status is',
      displayValue: option.label
    };
  }
}

export class OutboxMailboxFilter implements FilterDefinition<OutboxFilterType> {
  readonly type = 'mailbox' as const;
  readonly icon = InboxStackIcon;
  readonly priority = 5;
  readonly isUnique = true;
  private cachedAccounts: { id: string; label: string }[] | null = null;

  async generate({ query }: FilterGeneratorContext<OutboxFilterType>): Promise<FilterSuggestion[]> {
    if (this.cachedAccounts === null) {
      try {
        const result = await client.query<MailboxAccountsQuery, MailboxAccountsQueryVariables>({
          query: MailboxAccountsDocument,
          variables: {}
        });
        this.cachedAccounts = (result?.data?.mailboxAccounts ?? []).map((account) => ({
          id: account.id,
          label: account.identity.email || account.identity.displayName || account.id
        }));
      } catch {
        this.cachedAccounts = [];
      }
    }

    const q = query.trim().toLowerCase();
    return this.cachedAccounts
      .filter((account) => account.label.toLowerCase().includes(q) || 'mailbox'.includes(q))
      .map((account) => ({
        value: account.id,
        displayLabel: 'Mailbox is',
        displayValue: account.label
      }));
  }

  deserialize(s: SerializedFilter<OutboxFilterType>): ActiveFilter<OutboxFilterType> {
    return {
      id: `mailbox-${s.value}`,
      type: this.type,
      value: s.value,
      // A mailbox the caller can no longer see still deserializes, so a shared
      // link opens the view it names rather than silently dropping half of it.
      displayValue: s.displayValue || s.value,
      displayLabel: 'Mailbox is'
    };
  }
}

const BATCH_LABEL = 'Batch is';

/** The chip that narrows Outgoing to one batch's emails. */
export const batchFilter = (batch: {
  id: string;
  name: string;
}): SerializedFilter<OutboxFilterType> => ({
  type: 'batch',
  value: batch.id,
  displayValue: batch.name
});

/** The agency's recent batches, offered by name. A bulk draft isn't a batch until it's sent. */
export class OutboxBatchFilter implements FilterDefinition<OutboxFilterType> {
  readonly type = 'batch' as const;
  readonly icon = RectangleStackIcon;
  readonly priority = 3;
  readonly isUnique = true;
  private cachedBatches: EmailBulkSendListFieldsFragment[] | null = null;

  async generate({ query }: FilterGeneratorContext<OutboxFilterType>): Promise<FilterSuggestion[]> {
    if (this.cachedBatches === null) {
      // Thrown rather than cached as empty, so the next keystroke asks again.
      const { data, error } = await getEmailBulkSends();
      if (error) {
        throw error;
      }
      this.cachedBatches = (data ?? []).filter((bulkSend) => !!bulkSend.sentAt);
    }

    const q = query.trim().toLowerCase();
    return this.cachedBatches
      .filter((batch) => batch.name.toLowerCase().includes(q) || 'batch'.includes(q))
      .map((batch) => ({ value: batch.id, displayLabel: BATCH_LABEL, displayValue: batch.name }));
  }

  deserialize(s: SerializedFilter<OutboxFilterType>): ActiveFilter<OutboxFilterType> {
    return {
      id: `batch-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: BATCH_LABEL,
      displayValue: s.displayValue || s.value
    };
  }
}

const SELF_LABEL = 'Me';

/**
 * Who had a hand in the email: whoever opened the workspace it was composed
 * in, asked Cara for the draft, or committed the send.
 *
 * The agency roster comes from the page rather than from a query of its own,
 * because the outbox already holds it to name the sender in every row.
 */
export class OutboxPersonFilter implements FilterDefinition<OutboxFilterType> {
  readonly type = 'person' as const;
  readonly icon = UserIcon;
  readonly priority = 10;
  readonly isUnique = true;

  constructor(
    private readonly agencyUsers: { id: string; name: string; email: string }[],
    private readonly currentUserId: string
  ) {}

  private label(user: { id: string; name: string; email: string }): string {
    return user.id === this.currentUserId ? SELF_LABEL : user.name || user.email;
  }

  generate({ query }: FilterGeneratorContext<OutboxFilterType>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    // The caller first: "my email" is the question this filter is opened for.
    const ordered = [...this.agencyUsers].sort((a, b) =>
      a.id === this.currentUserId ? -1 : b.id === this.currentUserId ? 1 : 0
    );
    return ordered
      .filter((user) => {
        if (q.length === 0) {
          return true;
        }
        if (user.id === this.currentUserId && ('me'.startsWith(q) || 'mine'.startsWith(q))) {
          return true;
        }
        return (
          user.name.toLowerCase().includes(q) ||
          user.email.toLowerCase().includes(q) ||
          'person'.includes(q)
        );
      })
      .slice(0, 20)
      .map((user) => ({
        value: user.id,
        displayLabel: 'Person is',
        displayValue: this.label(user)
      }));
  }

  deserialize(s: SerializedFilter<OutboxFilterType>): ActiveFilter<OutboxFilterType> {
    const user = this.agencyUsers.find((candidate) => candidate.id === s.value);
    return {
      id: `person-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Person is',
      displayValue: user ? this.label(user) : s.displayValue || s.value
    };
  }
}

/**
 * Free text over what the email is called and who it went to. One chip only:
 * the server takes a single phrase, so two chips would have to be joined into
 * one, and the join would be a search for something neither asked for.
 */
export class OutboxSearchFilter implements FilterDefinition<OutboxFilterType> {
  readonly type = 'search' as const;
  readonly icon = MagnifyingGlassIcon;
  readonly priority = 100;
  readonly isUnique = true;

  generate({ query }: FilterGeneratorContext<OutboxFilterType>): FilterSuggestion[] {
    const q = query.trim();
    return q ? [{ value: q, displayLabel: 'Search for', displayValue: q }] : [];
  }

  deserialize(s: SerializedFilter<OutboxFilterType>): ActiveFilter<OutboxFilterType> {
    return {
      id: `search-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Search for',
      displayValue: s.displayValue || s.value
    };
  }
}

export class OutboxDateFilter extends BaseDateFilter<OutboxFilterType> {
  constructor() {
    super('date');
  }
}

export const createOutboxFilterDefinitions = (
  agencyUsers: { id: string; name: string; email: string }[],
  currentUserId: string,
  isScheduledSendEnabled = false,
  isBulkSendEnabled = false
): FilterDefinition<OutboxFilterType>[] => [
  new OutboxStatusFilter(isScheduledSendEnabled),
  new OutboxMailboxFilter(),
  ...(isBulkSendEnabled ? [new OutboxBatchFilter()] : []),
  new OutboxPersonFilter(agencyUsers, currentUserId),
  new OutboxDateFilter(),
  new OutboxSearchFilter()
];

export const outboxFilterBulkSendId = (
  filters: ActiveFilter<OutboxFilterType>[]
): string | undefined => filters.find((filter) => filter.type === 'batch')?.value;

export const outboxFilterStatuses = (
  filters: ActiveFilter<OutboxFilterType>[]
): OutboxStatusFilterValue[] =>
  filters
    .filter((filter) => filter.type === 'status')
    .map((filter) => filter.value as OutboxStatusFilterValue);

export const outboxFilterNamesClosedStatus = (filters: ActiveFilter<OutboxFilterType>[]): boolean =>
  outboxFilterStatuses(filters).some((status) => CLOSED_STATUS_FILTER_VALUES.has(status));

/**
 * The chips as the three source queries take them.
 *
 * `nowMs` is passed rather than read here so that the caller decides when the
 * boundary moves. It ends up in the query variables, and a value taken from a
 * clock that ticks would rewrite them on every tick, refetching all three
 * ledgers to ask the same question again.
 *
 * Undefined when nothing is selected, so an unfiltered outbox sends no filter
 * at all and the sources take the path they took before this existed.
 */
export function outboxFilterInput(
  filters: ActiveFilter<OutboxFilterType>[],
  opts: { nowMs: number }
): OutboxFilterInput | undefined {
  const input: OutboxFilterInput = {};

  const statuses = outboxFilterStatuses(filters);
  if (statuses.length > 0) {
    input.statuses = statuses;
    // Only when the answer turns on it. The two words are the same stored
    // state — a send written SENDING and never resolved — and only the age at
    // which the badge stops reading it as in flight tells them apart.
    if (
      statuses.includes(OutboxStatusFilterValue.Sending) ||
      statuses.includes(OutboxStatusFilterValue.Interrupted)
    ) {
      input.interruptedBefore = new Date(opts.nowMs - OUTBOUND_EMAIL_STUCK_AFTER_MS);
    }
  }

  for (const filter of filters) {
    switch (filter.type) {
      case 'search':
        input.query = filter.value;
        break;
      case 'mailbox':
        input.mailboxAccountID = filter.value;
        break;
      case 'person':
        input.actedByUserID = filter.value;
        break;
      case 'batch':
        input.bulkSendID = filter.value;
        break;
      case 'date': {
        const since = getSinceDate(filter.value);
        if (since) {
          input.activeAfter = since;
        }
        break;
      }
      default:
        break;
    }
  }

  return Object.keys(input).length === 0 ? undefined : input;
}
